/**
 * SettingsPage.tsx — /app/settings — account profile, password, GDPR
 *
 * Purpose:    Name/email profile edit (PATCH /v1/me), password change
 *             (POST /v1/me/password), GDPR data export (GET /v1/me/export →
 *             JSON download) and account deletion (DELETE /v1/me) with a
 *             type-your-email confirmation.
 * Inputs:     accountApi from @/lib/saas-org; useAuth for the session fallback
 * Outputs:    Account settings UI
 * Constraints: Dark-first; WCAG AA. Account routes are rolling out on the
 *              gateway — sections degrade to "coming online", never crash.
 * SOT:        nsentry SaaS completion spec § G-WEB (account + legal flows)
 */
import { useEffect, useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ComingOnline } from '@/components/ComingOnline'
import { accountApi, downloadJson } from '@/lib/saas-org'
import { useAuth } from '@/lib/auth-context'
import { isComingOnline } from '@/lib/sentry-error'

type Banner = { tone: 'ok' | 'error' | 'info'; text: string } | null

const BANNER_STYLES: Record<'ok' | 'error' | 'info', string> = {
  ok: 'border-green-200 dark:border-green-800/60 bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300',
  error: 'border-red-200 dark:border-red-800/60 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300',
  info: 'border-sky-200 dark:border-sky-800/60 bg-sky-50 dark:bg-sky-950/40 text-sky-700 dark:text-sky-300',
}

export function SettingsPage() {
  const { user, signOut } = useAuth()
  const navigate = useNavigate()
  const [profileOnline, setProfileOnline] = useState(true)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [banner, setBanner] = useState<Banner>(null)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [busy, setBusy] = useState<string | null>(null)

  useEffect(() => {
    void (async () => {
      const result = await accountApi.me()
      if (result.ok) {
        setName(result.value.name ?? '')
        setEmail(result.value.email || (user?.email ?? ''))
      } else {
        // Profile route rolling out — prefill from the session instead.
        setProfileOnline(!isComingOnline(result.error))
        setEmail(user?.email ?? '')
        setName(user?.displayName ?? '')
      }
    })()
  }, [])

  function report(tone: 'ok' | 'error' | 'info', text: string) {
    setBanner({ tone, text })
  }

  async function handleProfileSave(e: FormEvent) {
    e.preventDefault()
    setBusy('profile')
    const result = await accountApi.update({ name, email })
    setBusy(null)
    if (result.ok) report('ok', 'Profile saved.')
    else if (isComingOnline(result.error)) report('info', 'Profile editing is coming online — your change was not saved yet.')
    else report('error', result.error.message)
  }

  async function handlePasswordChange(e: FormEvent) {
    e.preventDefault()
    if (newPassword.length < 8) {
      report('error', 'New password must be at least 8 characters.')
      return
    }
    setBusy('password')
    const result = await accountApi.changePassword({ currentPassword, newPassword })
    setBusy(null)
    if (result.ok) {
      setCurrentPassword('')
      setNewPassword('')
      report('ok', 'Password updated.')
    } else if (isComingOnline(result.error)) {
      report('info', 'Password change is coming online — nothing was changed yet.')
    } else {
      report('error', result.error.message)
    }
  }

  async function handleExport() {
    setBusy('export')
    const result = await accountApi.exportData()
    setBusy(null)
    if (result.ok) {
      downloadJson(result.value, 'nsentry-data-export.json')
      report('ok', 'Your data export downloaded as nsentry-data-export.json.')
    } else if (isComingOnline(result.error)) {
      report('info', 'Data export is coming online. Until then, email privacy@nself.org and we will send your export within 30 days (see the GDPR page).')
    } else {
      report('error', result.error.message)
    }
  }

  async function handleDelete(e: FormEvent) {
    e.preventDefault()
    if (confirmEmail !== email) {
      report('error', 'Type your account email exactly to confirm deletion.')
      return
    }
    if (!confirm('Permanently delete this account and ALL its monitors, incidents, status pages, and data? This cannot be undone.')) return
    setBusy('delete')
    const result = await accountApi.deleteAccount(confirmEmail)
    setBusy(null)
    if (result.ok) {
      await signOut()
      navigate('/')
    } else if (isComingOnline(result.error)) {
      report('info', 'Self-serve deletion is coming online. Email privacy@nself.org from your account address and we will erase your data within 30 days (see the GDPR page).')
    } else {
      report('error', result.error.message)
    }
  }

  const inputClass =
    'w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-white focus-visible:ring-2 focus-visible:ring-sky-500 focus:outline-none'

  return (
    <div className="max-w-2xl p-6 lg:p-8">
      <h1 className="mb-6 text-xl font-semibold text-brand-ink">Settings</h1>

      {banner && (
        <p className={`mb-6 rounded-lg border px-4 py-3 text-sm ${BANNER_STYLES[banner.tone]}`} role={banner.tone === 'error' ? 'alert' : 'status'}>
          {banner.text}
        </p>
      )}

      <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Profile">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Profile</h2>
        {!profileOnline && (
          <div className="mb-4"><ComingOnline feature="Profile editing" /></div>
        )}
        <form onSubmit={handleProfileSave} className="space-y-4">
          <div>
            <label htmlFor="set-name" className="mb-1 block text-sm font-medium text-brand-secondary">Name</label>
            <input id="set-name" value={name} onChange={(e) => setName(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="set-email" className="mb-1 block text-sm font-medium text-brand-secondary">Email</label>
            <input id="set-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={inputClass} />
            <p className="mt-1 text-xs text-brand-muted">Changing your email changes your sign-in address.</p>
          </div>
          <button type="submit" disabled={busy === 'profile'} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50">
            {busy === 'profile' ? 'Saving…' : 'Save profile'}
          </button>
        </form>
      </section>

      <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Password">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-brand-muted">Password</h2>
        <form onSubmit={handlePasswordChange} className="space-y-4">
          <div>
            <label htmlFor="set-current-pw" className="mb-1 block text-sm font-medium text-brand-secondary">Current password</label>
            <input id="set-current-pw" type="password" required autoComplete="current-password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label htmlFor="set-new-pw" className="mb-1 block text-sm font-medium text-brand-secondary">New password</label>
            <input id="set-new-pw" type="password" required minLength={8} autoComplete="new-password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} className={inputClass} />
            <p className="mt-1 text-xs text-brand-muted">At least 8 characters.</p>
          </div>
          <button type="submit" disabled={busy === 'password'} className="rounded-md bg-sky-700 px-4 py-2 text-sm font-medium text-white hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50">
            {busy === 'password' ? 'Updating…' : 'Change password'}
          </button>
        </form>
      </section>

      <section className="mb-6 rounded-xl border border-brand-subtle bg-brand-surface p-6" aria-label="Your data">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-brand-muted">Your data</h2>
        <p className="mb-4 text-sm text-brand-muted">
          Download everything ɳSentry stores for your workspace as JSON. See the{' '}
          <Link to="/legal/gdpr" className="text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">GDPR page</Link> for what is included.
        </p>
        <button type="button" onClick={handleExport} disabled={busy === 'export'} className="rounded-md border border-brand-card px-4 py-2 text-sm font-medium text-brand-secondary hover:bg-brand-surface-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:opacity-50">
          {busy === 'export' ? 'Preparing…' : 'Download my data'}
        </button>
      </section>

      <section className="rounded-xl border border-red-900/60 bg-red-50 dark:bg-red-950/20 p-6" aria-label="Danger zone">
        <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-red-700 dark:text-red-400">Danger zone</h2>
        <p className="mb-4 text-sm text-brand-muted">
          Deleting your account permanently erases your workspace — monitors, incidents, status pages, API keys, and all telemetry. This cannot be undone.
        </p>
        <form onSubmit={handleDelete} className="space-y-3">
          <div>
            <label htmlFor="set-confirm-email" className="mb-1 block text-sm font-medium text-brand-secondary">
              Type your account email to confirm
            </label>
            <input id="set-confirm-email" type="email" value={confirmEmail} onChange={(e) => setConfirmEmail(e.target.value)} placeholder={email} className={inputClass} />
          </div>
          <button
            type="submit"
            disabled={busy === 'delete' || confirmEmail !== email || !email}
            className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white hover:bg-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-red-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy === 'delete' ? 'Deleting…' : 'Delete account permanently'}
          </button>
        </form>
      </section>
    </div>
  )
}
